'use client';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ReferenceCard } from './reference-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const ReferencesList = ({ locale }: { locale: string }) => {
  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 9,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  const { t } = useTranslations();

  return (
    <>
      <div
        className={cn('custom-container', 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3')}
      >
        {referencesQuery.isLoading && <ReferencesSkeleton />}
        {referencesQuery.isSuccess &&
          references.map(reference => <ReferenceCard reference={reference} key={reference.slug} />)}
      </div>

      {referencesQuery.hasNextPage && (
        <Button
          onClick={() => referencesQuery.fetchNextPage()}
          isLoading={referencesQuery.isFetchingNextPage}
          className={cn('mt-16', { hidden: !referencesQuery.hasNextPage })}
          variant={'outline'}
        >
          {t('text338')}
        </Button>
      )}
    </>
  );
};

const ReferencesSkeleton = () => {
  return (
    <div
      className={cn(
        'animate-pulse',
        'col-span-1 grid w-full grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3',
      )}
    >
      <div className="col-span-1 h-[215px] w-full bg-neutral-100 sm:h-[250px] lg:h-[275px] xl:h-[325px]" />
      <div className="col-span-1 h-[215px] w-full bg-neutral-100 sm:h-[250px] lg:h-[275px] xl:h-[325px]" />
      <div className="col-span-1 h-[215px] w-full bg-neutral-100 sm:h-[250px] lg:h-[275px] xl:h-[325px]" />
    </div>
  );
};
